"use client";

import { FlexColumn, FlexRowAlignCenter } from "@/components/Flex";
import PreviewFlashcardCard from "@/components/flashcards/flashcardsCard/PreviewFlashcardCard";
import InputWithSearchbarIcon from "@/components/styled/input/InputWithSearchbarIcon";
import { HorizontallyCenteredRepeatableLeaderboardAdSlot } from "@/features/Ads/components/FreestarAdSlot";
import { AuthEvent } from "@/features/Auth/AuthPopup/utils/constants";
import { useBreakPoints } from "@/hooks/styles/useBreakpoints";
import { useCheckAuth } from "@/hooks/useCheckAuth";
import useDOMEventListener from "@/hooks/useDOMEventListener";
import br from "@/styles/breakpoints.module.css";
import { themeColors } from "@/utils/themeColors";
import {
    ClassRole,
    ClassSection,
    Flashcard,
    FlashcardSet,
    FlashcardSetViewer,
    StudySession,
    StudySessionProgressEnum,
    StudySessionType,
} from "@knowt/syncing/graphql/schema";
import { useFlashcardSetViewer } from "@knowt/syncing/hooks/flashcardSetViewer/useFlashcardSetViewer";
import { useFlashcardSet } from "@knowt/syncing/hooks/flashcards/useFlashcards";
import { ORDERED_FLASHCARD_STUDY_SECTIONS } from "@knowt/syncing/hooks/study/constants";
import { useFlashcardStudyStates, useStudySession } from "@knowt/syncing/hooks/study/useStudySession";
import { useFlashcardSetStudyAnalytics } from "@knowt/syncing/hooks/study/useStudySessionStats";
import { canShowAds } from "@knowt/syncing/hooks/user/subscriptions";
import { LocalUser } from "@knowt/syncing/hooks/user/types";
import { objectWithout } from "@knowt/syncing/utils/dataCleaning";
import clsx from "clsx";
import { useCallback, useMemo, useRef, useState } from "react";
import { isMacOs } from "react-device-detect";
import { Virtuoso } from "react-virtuoso";
import { FlashcardItem, MixedAd } from "../types";
import { mergeProgressToFlashcards, mixAdsToFlashcards } from "../utils";
import { SectionBtn, StarBtn, ViewAllBtn } from "./SetOverviewFilterBtns";
import ListPadWrapper from "./styled/ListPadWrapper";
import LoadMoreBtn from "./styled/LoadMoreBtn";
import ToggleStarButton from "./styled/ToggleStarButton";
import LazyLoaded from "@/components/LazyLoaded";
import MasteryFlashcardDetailsPopup from "@/components/flashcards/flashcardsCard/MasteryFlashcardCard/MasteryFlashcardDetailsPopup";
import { useClass } from "@knowt/syncing/hooks/classes/useClass";

const INITIAL_MIN_FLASHCARDS_COUNT = 100;

type SetOverviewListFilter = "all" | "starred" | StudySessionProgressEnum;

const FlashcardListFilter = ({
    total,
    onQueryChange,
    filterBy,
    onFilterByChange,
    onToggleStarring,
}: {
    total: {
        all: number;
        filtered: number;
        starred: number;
        starredFiltered: number;
        [StudySessionProgressEnum.NEW]: number;
        [StudySessionProgressEnum.LEARNING]: number;
        [StudySessionProgressEnum.REVIEWING]: number;
        [StudySessionProgressEnum.MASTERED]: number;
    };
    filterBy: SetOverviewListFilter;
    onFilterByChange: (filterBy: SetOverviewListFilter) => void;
    onQueryChange: ({ query }: { query: string }) => void;
    onToggleStarring: (props: { isStarActive: boolean }) => void;
}) => {
    const { smDown } = useBreakPoints();
    const { checkAuth } = useCheckAuth();
    const inputRef = useRef<HTMLInputElement>(null);

    useDOMEventListener({
        on: "keydown",
        handler: async (event: KeyboardEvent) => {
            const isCtrlKey = isMacOs ? event.metaKey : event.ctrlKey;
            if (!(event.key === "F3" || (isCtrlKey && event.key === "f"))) return;
            event.preventDefault();

            inputRef.current?.focus();
        },
    });

    return (
        <FlexColumn>
            <FlexRowAlignCenter
                className={clsx(br.smDownColumn, br.smDownJustifyCenter)}
                style={{ marginBottom: "4rem", gap: "1.5rem" }}>
                <h3
                    className="heading5"
                    style={{ marginRight: "auto", flex: 1 }}
                    data-testid="auto-id-060c7f96-d103-4ebd-b34d-8e783bdcb070">
                    {total.all} Terms
                </h3>
                <InputWithSearchbarIcon
                    className={clsx("secondaryText1", smDown && br.smDownWidth100)}
                    ref={inputRef}
                    onChange={e => {
                        onQueryChange({ query: e.target.value });
                    }}
                    placeholder={"Search terms/definitions"}
                    containerStyle={{ flex: 1, width: "100%" }}
                    sx={{
                        fontWeight: "400",
                        width: "100%",
                        transition: "width 0.3s",
                        backgroundColor: themeColors.neutralWhite,
                    }}
                />
            </FlexRowAlignCenter>
            {/* ALL FILTER PILLS */}
            <FlexRowAlignCenter style={{ marginBottom: "4rem", flexWrap: "wrap", gap: "2.4rem" }}>
                <ViewAllBtn onClick={() => onFilterByChange("all")} isActive={filterBy === "all"} total={total.all} />

                {ORDERED_FLASHCARD_STUDY_SECTIONS.map(section => {
                    const numFlashcards = total[section];

                    if (!numFlashcards) return null;

                    return (
                        <SectionBtn
                            key={section}
                            section={section}
                            isActive={filterBy === section}
                            onClick={() => onFilterByChange(section)}
                            total={numFlashcards}
                        />
                    );
                })}

                {total.starred > 0 && (
                    <StarBtn
                        onClick={() => onFilterByChange("starred")}
                        isActive={filterBy === "starred"}
                        total={total.starred}
                    />
                )}

                <ToggleStarButton
                    sx={{ marginLeft: "auto" }}
                    allStarred={total[filterBy] === total.starredFiltered}
                    numOfFlashcards={total[filterBy]}
                    onClick={() => {
                        checkAuth({
                            event: AuthEvent.STAR_FLASHCARD,

                            onAuthSuccess: () =>
                                onToggleStarring({ isStarActive: total[filterBy] === total.starredFiltered }),
                        });
                    }}
                />
            </FlexRowAlignCenter>
        </FlexColumn>
    );
};

const SetOverviewFlashcardList = ({
    isTeacherClassSet,
    initialFlashcardSet,
    readOnly,
    teacherHasPremium,
    serverUser,
    serverFlashcardSetViewer,
    serverStudySession,
    isForMaterialStats,
    selectedSection,
}: {
    isTeacherClassSet?: boolean;
    initialFlashcardSet: FlashcardSet;
    readOnly: boolean;
    teacherHasPremium?: boolean;
    serverUser: LocalUser;
    serverFlashcardSetViewer?: FlashcardSetViewer;
    serverStudySession?: StudySession | null | undefined;
    isForMaterialStats?: boolean;
    selectedSection?: ClassSection | null | undefined;
}) => {
    const [query, setQuery] = useState("");
    const [filterBy, setFilterBy] = useState<SetOverviewListFilter>("all");
    const [shouldVirtualize, setShouldVirtualize] = useState(false);
    const [masteryDetailsPopupOpenForFlashcardIndex, setMasteryDetailsPopupOpenForFlashcardIndex] = useState<
        number | null
    >(null);

    const { flashcardSetViewerData, toggleFlashcardStarByIds } = useFlashcardSetViewer({
        flashcardSetId: initialFlashcardSet.flashcardSetId,
        fallbackData: serverFlashcardSetViewer,
    });

    const starred = useMemo(() => flashcardSetViewerData?.starred || [], [flashcardSetViewerData?.starred]);

    const { studySession } = useStudySession({
        flashcardSetId: initialFlashcardSet.flashcardSetId,
        type: StudySessionType.LEARN,
        fallbackData: serverStudySession,
    });

    const flashcardSetId = initialFlashcardSet.flashcardSetId;

    const { flashcardSet, flashcards } = useFlashcardSet({ flashcardSetId: flashcardSetId });
    const { flashcardStudyStates } = useFlashcardStudyStates({ flashcardSetId: flashcardSetId });
    const { flashcardsMastery, flashcardsStudyAnalytics } = useFlashcardSetStudyAnalytics({
        flashcardSet: initialFlashcardSet,
        isEnabled: isTeacherClassSet || isForMaterialStats,
        selectedSection,
    });

    const { course, currentUserClassRole } = useClass({ classId: initialFlashcardSet.classId });

    const canShowMasteryPill = flashcardSet?.classId && currentUserClassRole !== ClassRole.STUDENT;

    const filteredFlashcards: FlashcardItem[] = useMemo(() => {
        if (!flashcards) return [];

        const flashcardsWithProgress = mergeProgressToFlashcards({
            flashcards,
            flashcardStudyStates,
        });

        let results: Flashcard[] = shouldVirtualize
            ? flashcardsWithProgress
            : flashcardsWithProgress.slice(0, INITIAL_MIN_FLASHCARDS_COUNT);

        if (ORDERED_FLASHCARD_STUDY_SECTIONS.includes(filterBy as StudySessionProgressEnum)) {
            results = flashcardsWithProgress.filter(flashcard => flashcard.progress === filterBy);
        } else if (filterBy === "starred") {
            results = flashcardsWithProgress.filter(flashcard => starred.includes(flashcard.flashcardId));
        }

        if (query) {
            results = results.filter(f => {
                return (
                    f.term?.toLowerCase().includes(query.toLowerCase().trim()) ||
                    f.definition?.toLowerCase().includes(query.toLowerCase().trim())
                );
            });
        }

        return (results || []) as FlashcardItem[];
    }, [filterBy, flashcardStudyStates, flashcards, query, shouldVirtualize, starred]);

    const flashcardsOrAds: (FlashcardItem | MixedAd)[] = useMemo(() => {
        if (canShowAds(serverUser) && !teacherHasPremium && !isForMaterialStats) {
            return mixAdsToFlashcards({ flashcards: filteredFlashcards, interval: 5 });
        }

        return filteredFlashcards || [];
    }, [filteredFlashcards, serverUser]);

    const renderFilter = useCallback(() => {
        return (
            <FlashcardListFilter
                total={{
                    all: flashcards?.length || 0,
                    filtered: filteredFlashcards.length || 0,
                    starred: starred.length || 0,
                    starredFiltered: filteredFlashcards?.filter(f => starred.includes(f.flashcardId)).length || 0,
                    NEW: studySession?.progress?.[StudySessionProgressEnum.NEW] || 0,
                    LEARNING: studySession?.progress?.[StudySessionProgressEnum.LEARNING] || 0,
                    REVIEWING: studySession?.progress?.[StudySessionProgressEnum.REVIEWING] || 0,
                    MASTERED: studySession?.progress?.[StudySessionProgressEnum.MASTERED] || 0,
                }}
                onQueryChange={({ query }) => setQuery(query)}
                filterBy={filterBy}
                onFilterByChange={f => setFilterBy(f)}
                onToggleStarring={({ isStarActive }) => {
                    const ids = filteredFlashcards.map(f => f.flashcardId);
                    toggleFlashcardStarByIds(ids, !isStarActive);
                }}
            />
        );
    }, [filteredFlashcards, starred, studySession, toggleFlashcardStarByIds, flashcards?.length, filterBy]);

    const renderCard = useCallback(
        ({ item, index }: { item: FlashcardItem | MixedAd; index: number }) => {
            if ((item as MixedAd).type === "ad") {
                const ad = item as unknown as MixedAd;
                return (
                    <ListPadWrapper key={ad.type + ad.index} style={{ padding: "1rem 0" }}>
                        <HorizontallyCenteredRepeatableLeaderboardAdSlot slotId={ad.index} />
                    </ListPadWrapper>
                );
            }

            const flashcardItem = item as FlashcardItem;

            const progress = flashcardItem?.progress;
            const sort = flashcardItem?.sort;
            const order = flashcardItem.index;
            const flashcard = objectWithout(flashcardItem, "progress", "index") as Flashcard;

            const isFirst = index === 0;
            const isLast = index === flashcardsOrAds.length - 1;
            return (
                <ListPadWrapper isFirst={isFirst} isLast={isLast} key={flashcard.flashcardId + index}>
                    <PreviewFlashcardCard
                        index={order}
                        flashcard={flashcard}
                        readOnly={readOnly}
                        progress={progress}
                        sort={sort}
                        flashcardMastery={canShowMasteryPill ? flashcardsMastery?.[flashcard.flashcardId] : undefined}
                        onMasteryDetailsClick={() => setMasteryDetailsPopupOpenForFlashcardIndex(order)}
                    />
                </ListPadWrapper>
            );
        },
        [isTeacherClassSet, readOnly, flashcardsMastery, initialFlashcardSet, flashcardsOrAds, flashcardSet]
    );

    return (
        <FlexColumn>
            {!isForMaterialStats && renderFilter()}
            {shouldVirtualize ? (
                <Virtuoso
                    useWindowScroll
                    initialItemCount={Math.min(flashcardsOrAds.length, 100)}
                    data={flashcardsOrAds}
                    itemContent={(i, item) => renderCard({ item, index: i })}
                />
            ) : (
                flashcardsOrAds.map((item, i) => renderCard({ item, index: i }))
            )}

            {(flashcards?.length || 0) > INITIAL_MIN_FLASHCARDS_COUNT && !shouldVirtualize && (
                <LoadMoreBtn onClick={() => setShouldVirtualize(true)} sx={{ margin: "1rem auto 0" }} />
            )}
            <LazyLoaded load={masteryDetailsPopupOpenForFlashcardIndex !== null}>
                <MasteryFlashcardDetailsPopup
                    onClose={() => setMasteryDetailsPopupOpenForFlashcardIndex(null)}
                    currentFlashcardIndex={masteryDetailsPopupOpenForFlashcardIndex}
                    allFlashcards={flashcardSet?.flashcards as Flashcard[]}
                    members={course?.members || []}
                    flashcardsStudyAnalytics={flashcardsStudyAnalytics}
                />
            </LazyLoaded>
        </FlexColumn>
    );
};

export default SetOverviewFlashcardList;
